import React from "react";

const updatedDate = "May 16, 2026";

const styles = {
    page: {
        minHeight: "100vh",
        background:
            "radial-gradient(circle at top left, rgba(197,155,69,0.12), transparent 32%), radial-gradient(circle at top right, rgba(5,7,19,0.10), transparent 30%), linear-gradient(to bottom, #F7F8FA, #F2F3F5 45%, #F7F8FA)",
        color: "#0F172A",
        padding: "72px 20px",
        fontFamily:
            "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
    },
    container: {
        maxWidth: "1040px",
        margin: "0 auto",
        lineHeight: 1.75,
    },
    badge: {
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 12px",
        borderRadius: "999px",
        background: "#FFF9EA",
        color: "#7A5A17",
        border: "1px solid #E7D8AC",
        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        marginBottom: "18px",
    },
    h1: {
        fontSize: "clamp(32px, 4vw, 48px)",
        lineHeight: 1.08,
        margin: "0 0 14px",
        letterSpacing: "-0.04em",
        color: "#050713",
        fontWeight: 700,
    },
    updated: {
        color: "#64748B",
        marginBottom: "34px",
        fontSize: "14px",
    },
    h2: {
        fontSize: "18px",
        marginTop: "30px",
        marginBottom: "10px",
        color: "#050713",
        letterSpacing: "-0.015em",
        fontWeight: 700,
    },
    p: {
        color: "#475569",
        margin: "0 0 14px",
        fontSize: "15px",
    },
    ul: {
        color: "#475569",
        paddingLeft: "22px",
        marginTop: "8px",
        fontSize: "15px",
    },
    box: {
        border: "1px solid #E2E8F0",
        background: "#FFFFFF",
        borderRadius: "24px",
        padding: "24px",
        margin: "24px 0 32px",
        boxShadow: "0 18px 55px rgba(15, 23, 42, 0.06)",
    },
    document: {
        border: "1px solid #E2E8F0",
        background: "#FFFFFF",
        borderRadius: "30px",
        padding: "32px",
        boxShadow: "0 18px 55px rgba(15, 23, 42, 0.06)",
    },
    link: {
        color: "#7A5A17",
        textDecoration: "none",
        fontWeight: 700,
    },
};

export default function RefundPolicy() {
    return (
        <main style={styles.page}>
            <section style={styles.container}>
                <div style={styles.badge}>Krionex Legal</div>

                <h1 style={styles.h1}>Refund & Cancellation Policy</h1>
                <p style={styles.updated}>Last updated: {updatedDate}</p>

                <div style={styles.box}>
                    <p style={styles.p}>
                        This Refund & Cancellation Policy explains how cancellations,
                        subscription renewals, refunds, and blockchain-related costs are
                        handled for Krionex.
                    </p>
                    <p style={styles.p}>
                        Krionex is a software platform that provides tools for contract
                        deployment, token management, dashboards, and related product
                        workflows.
                    </p>
                </div>
                <div style={styles.document}>
                    <h2 style={styles.h2}>1. Subscriptions</h2>
                    <p style={styles.p}>
                        Paid Krionex plans are billed in advance on a monthly, yearly, or
                        other stated billing interval depending on the plan selected at
                        checkout.
                    </p>
                    <p style={styles.p}>
                        Your subscription gives you access to the paid software features
                        included in your selected plan for the active billing period.
                    </p>
                    <h2 style={styles.h2}>2. Cancellation</h2>
                    <p style={styles.p}>
                        You may cancel your subscription at any time through your account
                        settings, the Paddle customer portal, or by contacting Krionex support.
                    </p>
                    <p style={styles.p}>
                        Unless otherwise stated, cancellation prevents future renewals but does
                        not refund charges already paid for the current billing period. After
                        cancellation, you may continue to access paid features until the end of
                        the current billing period unless access is suspended for misuse, abuse,
                        fraud, chargeback abuse, or violation of our Terms.
                    </p>

                    <h2 style={styles.h2}>3. Refund Requests</h2>
                    <p style={styles.p}>
                        Subscription fees for the current billing period are generally
                        non-refundable once paid. Canceling a subscription stops future renewals
                        but does not automatically refund the current billing period.
                    </p>
                    <p style={styles.p}>
                        Refunds may be reviewed only in limited cases, such as duplicate billing,
                        verified payment errors, verified technical failure caused by Krionex,
                        requirements under applicable law, Paddle policy, or approval by Krionex
                        after review.
                    </p>
                    <p style={styles.p}>
                        Refunds may be denied where there is evidence of abuse, fraud, excessive
                        usage, chargeback abuse, violation of our Terms of Service, repeated refund
                        requests, user mistakes, wallet issues, deployment mistakes, or substantial
                        use of paid features.
                    </p>

                    <h2 style={styles.h2}>4. Non-Refundable Items</h2>
                    <p style={styles.p}>The following are non-refundable by Krionex:</p>
                    <ul style={styles.ul}>
                        <li>blockchain gas fees;</li>
                        <li>network fees, validator fees, and wallet fees;</li>
                        <li>failed, delayed, rejected, or duplicated blockchain transactions;</li>
                        <li>smart contract deployment costs paid to blockchain networks;</li>
                        <li>minting, burning, pausing, unpausing, or other on-chain action costs;</li>
                        <li>fees charged by third-party wallets, RPC providers, or blockchain networks;</li>
                        <li>losses caused by incorrect wallet addresses, wrong networks, user mistakes, or wallet issues.</li>
                    </ul>
                    <p style={styles.p}>
                        Blockchain transactions are generally irreversible. Krionex cannot
                        reverse, cancel, edit, hide, or refund on-chain transactions once they
                        are submitted to a blockchain network.
                    </p>

                    <h2 style={styles.h2}>5. Plan Changes</h2>
                    <p style={styles.p}>
                        If you upgrade your plan, the new plan may take effect immediately or
                        according to the billing rules shown at checkout.
                    </p>
                    <p style={styles.p}>
                        If you downgrade your plan, the downgrade may take effect immediately
                        or at the end of the current billing period, depending on the payment
                        provider and plan configuration.
                    </p>

                    <h2 style={styles.h2}>6. Payment Provider</h2>
                    <p style={styles.p}>
                        Subscription payments, invoices, taxes, renewals, refunds, and payment
                        disputes may be handled by a third-party payment provider or Merchant
                        of Record.
                    </p>
                    <p style={styles.p}>
                        Krionex does not store full credit card numbers or complete payment
                        card details on its own servers.
                    </p>

                    <h2 style={styles.h2}>7. How to Request a Refund</h2>
                    <p style={styles.p}>
                        To request a refund, contact us at{" "}
                        <a style={styles.link} href="mailto:support@krionex.com">
                            support@krionex.com
                        </a>
                        .
                    </p>
                    <p style={styles.p}>Please include:</p>
                    <ul style={styles.ul}>
                        <li>your account email;</li>
                        <li>payment date;</li>
                        <li>plan purchased;</li>
                        <li>reason for the refund request;</li>
                        <li>any relevant invoice, transaction, or subscription reference.</li>
                    </ul>

                    <h2 style={styles.h2}>8. Changes to This Policy</h2>
                    <p style={styles.p}>
                        We may update this Refund & Cancellation Policy from time to time. The
                        updated version will be posted on this page with a revised “Last
                        updated” date.
                    </p>
                </div>
            </section>
        </main>
    );
}